import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

// Store Custom Code
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper"; // Store Custom Code
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString"; // Store Custom Code
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext"; // Store Custom Code
import ApplicationState from "@insite/client-framework/Store/ApplicationState"; // Store Custom Code
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors"; // Store Custom Code
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo"; // Store Custom Code
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors"; // Store Custom Code
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code
import { ProductModel } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code
import { COLORS, SPACING, TYPOGRAPHY } from "@store/theme/design"; // Store Custom Code
import { skuNumber } from "@store/theme/mixins/typography"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux"; // Store Custom Code

// Store Custom Code - Start
const mapStateToProps = (state: ApplicationState) => ({
    currentShipTo: getCurrentShipToState(state).value,
    currentShipToState: getCurrentShipToState(state),
    userName: state.context.session?.userName,
    isGuest: state.context.session?.isGuest,
    location: getLocation(state),
    session: state.context.session,
});

const mapDispatchToProps = {
    loadCurrentShipTo,
};
// Store Custom Code - End

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Store Custom Code
    product?: ProductModel | CartLineModel | ProductContextModel | OrderLineModel;
    highlightSku?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> & // Store Custom Code
    ResolveThunks<typeof mapDispatchToProps> & // Store Custom Code
    HasProductContext; // Store Custom Code

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Store Custom Code
    erpNumberValueTextHighlight?: TypographyPresentationProps;
    extraErpNumberGridItem?: GridItemProps;
    extraErpNumberLabelText?: TypographyPresentationProps;
    extraErpNumberValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 5 },
    erpNumberLabelText: {
        // Store Custom Code - Remove weight: bold;
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    // Store Custom Code
    erpNumberGridItem: {
        css: css`
            span {
                ${skuNumber()}
            }
        `,
    },
    erpNumberValueTextHighlight: {
        weight: "bold",
        css: css`
            background: yellow;
        `,
    },
    extraErpNumberGridItem: {
        css: css`
            span {
                ${skuNumber()}
                font-size: ${TYPOGRAPHY.size.tiny};
            }
        `,
    },
    extraErpNumberLabelText: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    extraErpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            margin-right: ${SPACING.xsmall};
            ${wrapInContainerStyles}
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = false, // Store Custom Code
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Store Custom Code
    productContext,
    userName,
    isGuest,
    location,
    session,
    currentShipTo,
    currentShipToState,
    loadCurrentShipTo,
    product,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;
    let productData: any; // Store Custom Code

    // Store Custom Code - Start
    const [mainSku, setMainSku] = React.useState(productNumber);
    const [highlightSku, sethighlightSku] = React.useState(false);
    let extraSkus: string[] = [];
    const isAuthenticated = session && session.isAuthenticated && !session.rememberMe && !session.isGuest;

    React.useEffect(() => {
        // User is logged in, display SKUs based on market context
        if (isAuthenticated && !currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);
    React.useEffect(() => {
        if (product) {
            productData = product;
        } else if (productContext) {
            if ("product" in productContext) {
                productData = productContext.product;
            } else {
                productData = productContext;
            }
        }
        if (productData) {
            const productMainSku = productData.productNumber ? productData.productNumber : productData.erpNumber;
            const parseSkuList = (propertyName: string) => {
                if (!productData.properties[propertyName]) {
                    return [];
                }
                const ids: string = productData.properties[propertyName];
                const arrayIds: string[] = ids?.split(";");
                return arrayIds;
            };
            if (!userName && !isGuest) {
                // User is logged out, display all SKUs
                setMainSku(productMainSku);
                const ExtraSkusId = "sku-NYBS-CT"; // Todo: Refactor to allow more than two markets
                extraSkus = parseSkuList(ExtraSkusId);
            } else {
                const userMarket = currentShipTo?.properties?.customerMarket;
                if (userMarket) {
                    const productNumber = productMainSku;
                    const productSkuListForMarket = parseSkuList(`sku-${userMarket}`); // Search for an array of SKUs corresponding to market
                    if (productSkuListForMarket) {
                        // SKUs list was found
                        setMainSku(productSkuListForMarket[0] ? productSkuListForMarket[0] : productNumber); // First of the list is our main SKU
                        if (productSkuListForMarket.length > 1) {
                            extraSkus = productSkuListForMarket.filter(e => e !== mainSku); // Filter main SKU from list
                        }
                    } else {
                        // No SKU list for market: display productNumber
                        setMainSku(productMainSku);
                    }
                } else {
                    setMainSku(productMainSku);
                }
            }

            const siteSearchQueryString = parseQueryString<any>(location.search);
            if (
                siteSearchQueryString &&
                siteSearchQueryString.query &&
                siteSearchQueryString.query === productMainSku
            ) {
                sethighlightSku(true);
            } else {
                sethighlightSku(false);
            }
        }
    }, [currentShipTo]);
    // Store Custom Code - End

    return (
        <GridContainer {...styles.container}>
            {showProductNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && (
                        <Typography {...styles.erpNumberLabelText}>
                            {translate("SKU#") /* Store Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>
                                {translate("SKU#") /* Store Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {/* Store Custom Code - Start */}
                        {highlightSku ? (
                            <Typography {...styles.erpNumberValueTextHighlight}>{mainSku}</Typography>
                        ) : (
                            <>{mainSku}</>
                        )}
                        {/* Store Custom Code - End */}
                    </Typography>
                </GridItem>
            )}
            {/* Store Custom Code - Start */}
            {extraSkus && extraSkus.length > 0 && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.extraErpNumberGridItem}>
                    <StyledWrapper>
                        {showLabel && (
                            <Typography {...styles.extraErpNumberLabelText}>{translate("Extra SKUs#")}</Typography>
                        )}
                        {extraSkus.map((sku, index) => (
                            <Typography key={sku} {...styles.extraErpNumberValueText}>
                                {sku}
                            </Typography>
                        ))}
                    </StyledWrapper>
                </GridItem>
            )}
            {/* Store Custom Code - End */}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductPartNumbers)); // Store Custom Code
